import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios from "axios";

import {
  AUTH_BEARER_PREFIX,
  AUTH_REFRESH_TIMEOUT_MS,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_UNAUTHORIZED,
} from "../constants/app";
import { LICENSE_EXPIRED_ERROR_CODE } from "../constants/license";
import type { IRefreshTokenResponse } from "../types/auth";
import type { TClientPlatform } from "../types/client-platform";
import { describeRequestError } from "../utils/describe-request-error";
import {
  assertNoDotPathSegment,
  hasDotPathSegment,
} from "../utils/encode-path-segment";
import { readJwtSubject } from "../utils/jwt-subject";
import type { IStorageAdapter } from "../utils/storage";
import { StorageService } from "../utils/storage";

// NOTE: `skipAuthHandling` lets a request opt out of the 401 refresh/redirect
// and license-expired handling below. Used by anonymous public-share requests
// (NSAI-3853): a 401 there means a bad share password, not a signed-out user,
// so it must not clear storage or bounce the visitor to the sign-in page.
declare module "axios" {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- axios module augmentation requires the exact interface name
  export interface AxiosRequestConfig {
    skipAuthHandling?: boolean;
  }
}

// NOTE: NSAI-5230 — the request a non-axios transport needs to reproduce a call
// this client would have made: an absolute URL and the headers the request
// interceptor adds. See `ApiClient.resolveAuthorizedRequest`.
export interface IResolveAuthorizedRequestProps {
  url: string;
  params?: Record<string, unknown>;
}

export interface IAuthorizedRequest {
  url: string;
  headers: Record<string, string>;
}

// NOTE: Reproduces axios's own query serialization for the shapes this client
// sends, so a request issued outside axios reaches the backend as the same
// bytes the axios one would. Verified against the `encode` in axios
// 1.20.0's `helpers/buildURL.js`, which percent-encodes and then maps four
// sequences back: `%3A`→`:`, `%24`→`$`, `%2C`→`,` and `%20`→`+`. Brackets are
// NOT in that set — an array param leaves axios as `ids%5B%5D=1`, not
// `ids[]=1`, which is why nothing is un-escaped here beyond those four.
//
// NOTE: A primitive becomes `key=value`; an array repeats its key with the `[]`
// suffix axios appends; a param with no value is dropped rather than sent as
// the string "undefined". Nested objects are not serialized — no caller passes
// one, and stringifying `[object Object]` into a query silently would be worse
// than leaving it unsupported.
const encodeQueryComponent = (value: string): string =>
  encodeURIComponent(value)
    .replace(/%3A/gi, ":")
    .replace(/%24/g, "$")
    .replace(/%2C/gi, ",")
    .replace(/%20/g, "+");

const serializeQueryParams = (params: Record<string, unknown>): string =>
  Object.entries(params)
    .flatMap(([key, value]) => {
      if (value === undefined || value === null) {
        return [];
      }

      const values = Array.isArray(value) ? value : [value];
      const encodedKey = encodeQueryComponent(
        Array.isArray(value) ? `${key}[]` : key,
      );

      return values.map(
        (entry) => `${encodedKey}=${encodeQueryComponent(String(entry))}`,
      );
    })
    .join("&");

// NOTE: NSAI-6810 — the one refresh outcome that means the session is over:
// the server refused the refresh credential (401). Everything else — a 429
// from the rate limiter, a 5xx, a dropped connection — is transient.
const isRefreshCredentialRejected = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === HTTP_STATUS_UNAUTHORIZED;

const isLicenseExpiredError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === HTTP_STATUS_FORBIDDEN &&
  error.response?.data?.errorCode === LICENSE_EXPIRED_ERROR_CODE;

// NOTE: NSAI-6847 — what the 401 path marks a request it retries with: that
// it was retried once already, and the access token whose account it checked.
interface IRetriedRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryAccessToken?: string;
}

interface IIsSameAccountProps {
  sentAccessToken: string | undefined;
  retryAccessToken: string;
}

// NOTE: NSAI-6847 — may a request that left with one access token be retried
// with another? Only when both were issued to the same account: a request in
// flight while its user signed out and someone else signed in (another tab,
// or the same app) must not run as the new user. Only positive evidence of a
// different account refuses the retry — a token whose account cannot be read
// (one the server did not mint as a JWT) keeps the retry as it was.
const isSameAccount = ({
  sentAccessToken,
  retryAccessToken,
}: IIsSameAccountProps): boolean => {
  const sentSubject =
    sentAccessToken === undefined ? null : readJwtSubject(sentAccessToken);
  const retrySubject = readJwtSubject(retryAccessToken);

  return (
    sentSubject === null ||
    retrySubject === null ||
    sentSubject === retrySubject
  );
};

const readBearerToken = (authorization: unknown): string | undefined =>
  typeof authorization === "string" &&
  authorization.startsWith(AUTH_BEARER_PREFIX)
    ? authorization.slice(AUTH_BEARER_PREFIX.length)
    : undefined;

// NOTE: NSAI-6847 — the session one shared refresh is for: the credentials
// storage held when it started. See `ApiClient.isStillStored`.
interface IStoredSession {
  accessToken: string | null;
  refreshToken: string | null;
}

interface IHandleRefreshFailureProps {
  refreshError: unknown;
  refreshedSession: IStoredSession;
}

// NOTE: NSAI-6810 — what one shared refresh came to. Every request that waited
// on it reads the same outcome, so a failed refresh is handled once rather than
// once per waiting request.
interface IRefreshSucceeded {
  isRefreshed: true;
  accessToken: string;
}

interface IRefreshFailed {
  isRefreshed: false;
  refreshError: unknown;
  // NOTE: The license handler is taking the user to the license page; the
  // waiting requests stay pending instead of failing into the screen.
  isNavigatingAway: boolean;
}

type TRefreshOutcome = IRefreshSucceeded | IRefreshFailed;

export interface IApiClientConfig {
  baseURL: string;
  storageAdapter: IStorageAdapter;
  shouldStoreRefreshToken: boolean;
  // NOTE: NSAI-4330 — when set, resolved on EVERY request (and on the refresh
  // and keepalive paths) instead of `baseURL`, which then only seeds axios.
  // Mobile passes it so a deployment URL the user switched to at runtime takes
  // effect without rebuilding the client or the many modules holding it.
  getBaseURL?: () => string;
  // NOTE: NSAI-4330 — awaited before each request leaves. Mobile hands it the
  // storage-hydration promise so the first requests of a launch (fired before
  // AsyncStorage has been read) still see a persisted base URL override rather
  // than racing it to the wire.
  beforeRequest?: () => Promise<void>;
  defaultLanguage?: string;
  // NOTE: Client surface this app is. Sent as X-App-Platform so the server can
  // serve platform-appropriate copy (currently the FAQ corpus — see the server's
  // faq-data.platform). Omitted means the server assumes the desktop web app.
  platform?: TClientPlatform;
  getLanguage?: () => string | null;
  getProjectUuid?: () => string | null;
  // NOTE: Optional callback for E2E testing - only used when E2E_MODE is enabled
  getTestScenario?: () => string | null;
  onUnauthorized?: () => void;
  onLicenseExpired?: () => boolean;
  onTokenRefresh?: (accessToken: string) => void;
}

export interface IPostStreamProps {
  url: string;
  data: unknown;
  signal?: AbortSignal;
}

export class ApiClient {
  private readonly api: AxiosInstance;
  private readonly defaultLanguage: string;
  private readonly platform?: TClientPlatform;
  private readonly storageService: StorageService;
  private readonly getLanguage?: () => string | null;
  private readonly getProjectUuid?: () => string | null;
  private readonly getTestScenario?: () => string | null;
  private readonly onUnauthorized?: () => void;
  private readonly onLicenseExpired?: () => boolean;
  private readonly onTokenRefresh?: (accessToken: string) => void;
  private readonly shouldStoreRefreshToken: boolean;
  private readonly getBaseURL?: () => string;
  private readonly beforeRequest?: () => Promise<void>;
  // NOTE: NSAI-6810 — the refresh every concurrent 401 waits on; see
  // `refreshAccessToken`.
  private refreshInFlight: Promise<TRefreshOutcome> | null = null;

  constructor(config: IApiClientConfig) {
    const {
      baseURL,
      getBaseURL,
      beforeRequest,
      storageAdapter,
      defaultLanguage,
      platform,
      getLanguage,
      getProjectUuid,
      getTestScenario,
      onUnauthorized,
      onLicenseExpired,
      onTokenRefresh,
      shouldStoreRefreshToken,
    } = config;

    this.storageService = new StorageService(storageAdapter);
    this.defaultLanguage = defaultLanguage ?? "en";
    this.platform = platform;
    this.getLanguage = getLanguage;
    this.getProjectUuid = getProjectUuid;
    this.getTestScenario = getTestScenario;
    this.onUnauthorized = onUnauthorized;
    this.onLicenseExpired = onLicenseExpired;
    this.onTokenRefresh = onTokenRefresh;
    this.shouldStoreRefreshToken = shouldStoreRefreshToken;
    this.getBaseURL = getBaseURL;
    this.beforeRequest = beforeRequest;

    this.api = axios.create({
      baseURL,
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  // NOTE: NSAI-4330 — the one answer to "where is the API right now", for the
  // paths that bypass the request interceptor (token refresh, keepalive).
  private resolveBaseURL(): string {
    return this.getBaseURL?.() ?? this.api.defaults.baseURL ?? "";
  }

  // NOTE: NSAI-6810 — single-flight. When the access token lapses, every
  // request a screen has in flight comes back 401 at once; each used to post
  // its own refresh, and the server rate-limits that route per IP, so a busy
  // screen could trip its own limit. They now share one refresh, and
  // `onTokenRefresh` fires once per new token rather than once per request.
  // A failed refresh is settled here too, so the sign-out or license handler
  // runs once for the refresh, not again for every request waiting on it.
  private refreshAccessToken(): Promise<TRefreshOutcome> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const refreshedSession = this.readStoredSession();
    this.refreshInFlight = this.requestTokenRefresh(refreshedSession)
      .then(
        (accessToken: string): TRefreshOutcome => ({
          isRefreshed: true,
          accessToken,
        }),
      )
      .catch(
        (refreshError: unknown): TRefreshOutcome => ({
          isRefreshed: false,
          refreshError,
          isNavigatingAway: this.handleRefreshFailure({
            refreshError,
            refreshedSession,
          }),
        }),
      )
      .finally(() => {
        this.refreshInFlight = null;
      });

    return this.refreshInFlight;
  }

  private readStoredSession(): IStoredSession {
    return {
      accessToken: this.storageService.getToken(),
      refreshToken: this.storageService.getRefreshToken(),
    };
  }

  // NOTE: NSAI-6847 — a refresh answers for the session stored when it started,
  // so its outcome is applied only while storage still holds that session
  // (found in review). A sign-in or sign-out that landed meanwhile — in another
  // tab sharing storage, or in the app itself — has replaced it: the refreshed
  // token belongs to a session the client no longer holds, and a refusal says
  // nothing about the one it holds now.
  private isStillStored(session: IStoredSession): boolean {
    const storedSession = this.readStoredSession();

    return (
      storedSession.accessToken === session.accessToken &&
      storedSession.refreshToken === session.refreshToken
    );
  }

  // NOTE: NSAI-6847 — the license handler is the app's code. It runs inside
  // the shared refresh, where a throw would reject the refresh every waiting
  // request is reading: each would fail with the handler's error instead of
  // the refresh failure. A handler that throws is treated as not navigating.
  private notifyLicenseExpired(): boolean {
    try {
      return this.onLicenseExpired?.() ?? false;
    } catch (handlerError) {
      console.error(
        "ApiClient: onLicenseExpired threw",
        describeRequestError(handlerError),
      );
      return false;
    }
  }

  // NOTE: NSAI-6810 — runs once per failed refresh (see `refreshAccessToken`)
  // and answers whether the license handler is navigating away.
  private handleRefreshFailure({
    refreshError,
    refreshedSession,
  }: IHandleRefreshFailureProps): boolean {
    // NOTE: If refresh failed because the license expired, redirect to the
    // license-expired page instead of the sign-in page.
    if (isLicenseExpiredError(refreshError) && this.onLicenseExpired) {
      return this.notifyLicenseExpired();
    }

    // NOTE: NSAI-6810 — only the server refusing the refresh credential itself
    // (unknown, expired or revoked session, banned user) ends the session. A
    // 429, a 5xx, a 400 or a dropped connection says nothing about the
    // session: signing out on them turned a rate-limited burst of refreshes
    // into a forced sign-in, which SSO users pay for with a full IdP prompt.
    // The request fails, the session stays, and the next request refreshes
    // again.
    if (!isRefreshCredentialRejected(refreshError)) {
      console.warn(
        "Token refresh failed; keeping the session",
        describeRequestError(refreshError),
      );
      return false;
    }

    // NOTE: NSAI-6847 — the refused credential is the refreshed session's. A
    // session that replaced it meanwhile is not cleared and nobody is signed
    // out: another account that signed in stays signed in, and a sign-out has
    // already done its own clearing.
    if (!this.isStillStored(refreshedSession)) {
      console.warn(
        "Refresh token rejected for a session no longer stored; keeping the current one",
        describeRequestError(refreshError),
      );
      return false;
    }

    console.error("Refresh token rejected", describeRequestError(refreshError));
    this.storageService.clearStorage();
    if (this.onUnauthorized) {
      this.onUnauthorized();
    }
    return false;
  }

  private async requestTokenRefresh(
    refreshedSession: IStoredSession,
  ): Promise<string> {
    const { refreshToken } = refreshedSession;
    const response = await axios.post<IRefreshTokenResponse>(
      `${this.resolveBaseURL()}/auth/refresh`,
      refreshToken ? { refreshToken } : {},
      { withCredentials: true, timeout: AUTH_REFRESH_TIMEOUT_MS },
    );
    const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
      response.data;

    // NOTE: NSAI-6847 — not over a session that replaced the refreshed one
    // (see `isStillStored`). The requests that waited on this refresh still
    // get the token, and the 401 path checks it against their own account.
    if (!this.isStillStored(refreshedSession)) {
      return newAccessToken;
    }

    this.storageService.setToken(newAccessToken);
    if (newRefreshToken && this.shouldStoreRefreshToken) {
      this.storageService.setRefreshToken(newRefreshToken);
    }

    if (this.onTokenRefresh) {
      this.onTokenRefresh(newAccessToken);
    }

    return newAccessToken;
  }

  // NOTE: NSAI-5230 — the headers every request of this client carries, built
  // in one place so the request interceptor below and `resolveAuthorizedRequest`
  // cannot drift. A request issued outside axios that is missing one of these
  // does not fail loudly: it comes back 401, or in the wrong language, or
  // against the wrong project.
  private buildRequestHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "X-App-Language":
        this.getLanguage?.() ||
        this.storageService.getLanguage() ||
        this.defaultLanguage,
    };

    if (this.platform) {
      headers["X-App-Platform"] = this.platform;
    }

    const token = this.storageService.getToken();
    if (token) {
      headers["Authorization"] = `${AUTH_BEARER_PREFIX}${token}`;
      headers["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
      headers["X-Project-Uuid"] =
        this.getProjectUuid?.() || this.storageService.getProjectUuid() || "";
    }

    // NOTE: Only add test header if callback provided AND returns value
    const testScenario = this.getTestScenario?.();
    if (testScenario) {
      headers["X-Test-Scenario"] = testScenario;
    }

    return headers;
  }

  private setupInterceptors(): void {
    // NOTE: Request interceptor
    this.api.interceptors.request.use(
      async (config: IRetriedRequestConfig) => {
        // NOTE: NSAI-5411 — a path identifier that escaped encoding (a crafted
        // route param such as `../../chat-folders/<uuid>/delete?x#`) would be
        // normalized by the URL parser onto another endpoint, and this request
        // would carry the user's bearer token there. Refuse it before the
        // headers are attached rather than trust every call site to encode.
        assertNoDotPathSegment(config.url);

        if (this.beforeRequest) {
          // NOTE: a rejected readiness promise must not take every later
          // request down with it (a promise's rejection is cached) — the
          // request goes out against whatever base URL resolves right now.
          try {
            await this.beforeRequest();
          } catch (error) {
            console.error("ApiClient: beforeRequest rejected", error);
          }
        }

        if (this.getBaseURL) {
          config.baseURL = this.getBaseURL();
        }

        for (const [name, value] of Object.entries(
          this.buildRequestHeaders(),
        )) {
          config.headers[name] = value;
        }

        // NOTE: NSAI-6847 — a retry carries the token whose account the 401
        // path checked, not whatever storage holds by now: `beforeRequest` is
        // awaited above, and a sign-in elsewhere can land in between (found in
        // review).
        if (config._retryAccessToken !== undefined) {
          config.headers["Authorization"] =
            `${AUTH_BEARER_PREFIX}${config._retryAccessToken}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // NOTE: Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config as IRetriedRequestConfig;

        // NOTE: Requests that manage their own auth (anonymous public-share
        // calls) opt out of every session-level handler below — no refresh, no
        // sign-in redirect, no license-expired bounce. The caller handles the
        // status code directly.
        if (originalRequest?.skipAuthHandling) {
          return Promise.reject(error);
        }

        // NOTE: Intercept license-expired 403s before other error handling
        if (isLicenseExpiredError(error) && this.onLicenseExpired) {
          const isNavigating = this.notifyLicenseExpired();
          // NOTE: If navigating away, return a never-resolving promise to keep
          // React Query in loading state and prevent ProtectedRoute from redirecting
          // to sign-in before the browser navigation completes.
          if (isNavigating) {
            return new Promise(() => {});
          }
          return Promise.reject(error);
        }

        if (
          error.response?.status === HTTP_STATUS_UNAUTHORIZED &&
          !originalRequest._retry
        ) {
          const refreshToken = this.storageService.getRefreshToken();
          const accessToken = this.storageService.getToken();

          const shouldSkipRefresh = this.shouldStoreRefreshToken
            ? !refreshToken && !accessToken
            : !accessToken;
          // NOTE: If we have no auth tokens at all, we're effectively signed out.
          // In that case, don't try to refresh – just propagate unauthorized.
          if (shouldSkipRefresh) {
            if (this.onUnauthorized) {
              this.onUnauthorized();
            }
            return Promise.reject(error);
          }

          originalRequest._retry = true;
          const sentAccessToken = readBearerToken(
            originalRequest.headers?.["Authorization"],
          );

          // NOTE: NSAI-6810 — the request left with a token that a refresh has
          // replaced since (in this tab, or another one sharing storage): the
          // stored token is the answer, so retry with it instead of spending
          // another refresh. NSAI-6847 — unless that token is another
          // account's; the retry carries the very token checked here.
          if (accessToken && sentAccessToken !== accessToken) {
            if (
              !isSameAccount({ sentAccessToken, retryAccessToken: accessToken })
            ) {
              return Promise.reject(error);
            }

            originalRequest._retryAccessToken = accessToken;
            return this.api(originalRequest);
          }

          const refreshOutcome = await this.refreshAccessToken();

          if (refreshOutcome.isRefreshed) {
            // NOTE: NSAI-6847 — the refresh credential can be another
            // account's too (the web's cookie, after a sign-in elsewhere).
            if (
              !isSameAccount({
                sentAccessToken,
                retryAccessToken: refreshOutcome.accessToken,
              })
            ) {
              return Promise.reject(error);
            }

            originalRequest._retryAccessToken = refreshOutcome.accessToken;
            return this.api(originalRequest);
          }

          // NOTE: Left pending while the license handler navigates away, as
          // the license-expired 403 branch above does.
          if (refreshOutcome.isNavigatingAway) {
            return new Promise(() => {});
          }

          return Promise.reject(refreshOutcome.refreshError);
        }

        return Promise.reject(error);
      },
    );
  }

  get instance(): AxiosInstance {
    return this.api;
  }

  // NOTE: NSAI-6810 — the refresh credential a client keeps itself (mobile,
  // the extension), or null where the browser holds it as an httpOnly cookie
  // (web). Signing out sends it, so the server can end the session even after
  // the access token has expired.
  getStoredRefreshToken(): string | null {
    return this.shouldStoreRefreshToken
      ? this.storageService.getRefreshToken()
      : null;
  }

  get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config);
  }

  delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }

  // NOTE: Authenticated POST that returns the raw fetch Response so the caller
  // can consume the body incrementally (response.body reader) — axios buffers
  // the entire body in the browser, which defeats streaming playback
  // (NSAI-1104/1105: Read Aloud must start sounding on the first audio chunk,
  // not after the last one is synthesized). Token auto-refresh does not run
  // here; a 401 surfaces to the caller as a non-ok Response.
  //
  // NOTE: NSAI-4330 / NSAI-5230 — base URL and headers come from the same two
  // helpers every other non-axios path uses. Reading `api.defaults.baseURL`
  // directly would send Read Aloud to the deployment baked into the bundle
  // after a "Change tenant", and a hand-rolled header set silently drops
  // `X-App-Platform`, `X-Timezone` and `X-Test-Scenario`.
  async postStream({ url, data, signal }: IPostStreamProps): Promise<Response> {
    // NOTE: NSAI-5411 — same guard the request interceptor applies; this path
    // bypasses it.
    assertNoDotPathSegment(url);
    const baseURL = this.resolveBaseURL();

    return fetch(`${baseURL}${url}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...this.buildRequestHeaders(),
      },
      body: JSON.stringify(data),
      ...(signal ? { signal } : {}),
    });
  }

  // NOTE: NSAI-5230 — the absolute URL and headers this client would have sent
  // for a GET, handed to a transport that cannot go through axios. The one
  // caller today is mobile's connector download, which streams a provider file
  // straight to disk through React Native's native downloader instead of
  // materialising it in the JS heap (see `use-attachment-download.ts`).
  //
  // NOTE: What a caller gives up by leaving axios: the response interceptor's
  // 401 refresh-and-retry. A request issued this way that races an expiring
  // access token comes back 401 and stays failed, so the caller must report it
  // as a failure the user can retry rather than assume it cannot happen.
  resolveAuthorizedRequest({
    url,
    params,
  }: IResolveAuthorizedRequestProps): IAuthorizedRequest {
    // NOTE: `url` is assumed relative to `baseURL`, as it is for every axios
    // method on this client. An absolute one would concatenate into nonsense
    // rather than fail, so callers pass the same paths they would pass `get`.
    //
    // NOTE: NSAI-4330 — resolved through `resolveBaseURL()` so a download
    // issued after "Change tenant" targets the selected deployment, not the
    // one baked into the bundle. Like `postKeepalive`, this does not await
    // `beforeRequest`: a caller reaches here from a screen that has already
    // made ordinary requests, so storage hydration has long since settled.
    // NOTE: NSAI-5411 — same guard the request interceptor applies.
    assertNoDotPathSegment(url);
    const baseURL = this.resolveBaseURL();
    const query = params ? serializeQueryParams(params) : "";
    const separator = url.includes("?") ? "&" : "?";

    return {
      url: `${baseURL}${url}${query ? `${separator}${query}` : ""}`,
      headers: this.buildRequestHeaders(),
    };
  }

  // NOTE: Fire-and-forget POST designed for `pagehide`/`beforeunload`. Uses
  // native `fetch` with `keepalive: true` so the browser completes the request
  // after the document unloads. Axios cannot do this (keepalive is not an
  // XMLHttpRequest option); `sendBeacon` can but cannot carry Bearer auth.
  // Token expiry and auto-refresh do NOT run here — any failure is silent, which
  // is acceptable because the S3 lifecycle rule is the orphan backstop.
  postKeepalive(url: string, data: unknown): void {
    // NOTE: NSAI-5411 — same guard the request interceptor applies. Logged and
    // dropped instead of thrown: this runs inside an unload handler.
    if (hasDotPathSegment(url)) {
      console.warn("postKeepalive skipped: dot path segment in request URL");
      return;
    }

    const token = this.storageService.getToken();
    if (!token) {
      // NOTE: No auth token — we can't issue the POST during unload (refresh
      // flows can't run synchronously here). Silent fallback: the S3 lifecycle
      // rule catches any orphaned uploads within 24h. Log for observability.
      console.warn(`postKeepalive skipped: no auth token available for ${url}`);
      return;
    }

    // NOTE: NSAI-4330 — resolved without awaiting `beforeRequest`, unlike the
    // interceptor: this runs during page unload, where nothing asynchronous
    // can be waited for, and a keepalive beacon implies an earlier request
    // already saw readiness settle.
    const baseURL = this.resolveBaseURL();
    const projectUuid =
      this.getProjectUuid?.() || this.storageService.getProjectUuid() || "";

    // NOTE: Intentionally unawaited — this runs during page unload and the
    // browser flushes it on our behalf. A pending Promise would be garbage-
    // collected before it resolves in that context.
    void fetch(`${baseURL}${url}`, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${AUTH_BEARER_PREFIX}${token}`,
        "X-Project-Uuid": projectUuid,
      },
      body: JSON.stringify(data),
    }).catch(() => {
      // NOTE: Page is unloading — we can't surface anything to the user anyway.
    });
  }
}
